/**
 * UI module - handles DOM manipulation and user interface updates
 */

import { COMMON_CONFUSIONS } from './morse.js';
import { ZONE_LOW, ZONE_HIGH } from './stats.js';

/**
 * Map an accuracy percentage to a gauge needle angle in degrees.
 * The semicircle spans -90..90 with three equal 60-degree zones:
 * too hard (below ZONE_LOW), optimal, and too easy (above ZONE_HIGH).
 * @param {number} accuracy - Accuracy percentage (0-100)
 * @returns {number} Needle angle in degrees
 */
export function accuracyToAngle(accuracy) {
    if (accuracy < ZONE_LOW) {
        return -90 + (accuracy / ZONE_LOW) * 60;
    }
    if (accuracy <= ZONE_HIGH) {
        return -30 + ((accuracy - ZONE_LOW) / (ZONE_HIGH - ZONE_LOW)) * 60;
    }
    return 30 + ((accuracy - ZONE_HIGH) / (100 - ZONE_HIGH)) * 60;
}

/**
 * Cache all DOM elements
 * @returns {Object} DOM element references
 */
export function initializeDOMElements() {
    return {
        charDisplay: document.getElementById('char-display'),
        listeningIndicator: document.getElementById('listening-indicator'),
        startButton: document.getElementById('start-btn'),
        stopButton: document.getElementById('stop-btn'),
        oopsButton: document.getElementById('oops-btn'),
        oopsChar: document.getElementById('oops-char'),
        practiceContent: document.getElementById('practice-content'),
        practiceCard: document.getElementById('practice-card'),
        timerDisplay: document.getElementById('timer'),
        characterSpeedSlider: document.getElementById('character-speed'),
        toneFrequencySlider: document.getElementById('tone-frequency'),
        toneVolumeSlider: document.getElementById('tone-volume'),
        voiceVolumeSlider: document.getElementById('voice-volume'),
        pauseDurationSlider: document.getElementById('pause-duration'),
        reviewDurationSlider: document.getElementById('review-duration'),
        speedDisplay: document.getElementById('speed-display'),
        toneDisplay: document.getElementById('tone-display'),
        toneVolumeDisplay: document.getElementById('tone-volume-display'),
        voiceVolumeDisplay: document.getElementById('voice-volume-display'),
        voiceSelect: document.getElementById('voice-select'),
        voiceDisplay: document.getElementById('voice-display'),
        voiceHelperText: document.getElementById('voice-helper-text'),
        pauseDisplay: document.getElementById('pause-display'),
        reviewDisplay: document.getElementById('review-display'),
        settingsBtn: document.getElementById('settings-btn'),
        settingsPanel: document.getElementById('settings-panel'),
        closeSettings: document.getElementById('close-settings'),
        resetProgressBtn: document.getElementById('reset-progress'),
        // Dashboard elements
        welcomeBox: document.getElementById('welcome-box'),
        gaugeContainer: document.getElementById('gauge-container'),
        gaugeContent: document.getElementById('gauge-content'),
        focusQueue: document.getElementById('focus-queue'),
        focusQueueGrid: document.getElementById('focus-queue-grid'),
        practiceStreamItems: document.getElementById('practice-stream-items'),
        settingsRecognition: document.getElementById('settings-recognition'),
        settingsSpeed: document.getElementById('settings-speed'),
        // Session summary
        sessionSummary: document.getElementById('session-summary'),
        dismissSummary: document.getElementById('dismiss-summary'),
        summaryChars: document.getElementById('summary-chars'),
        summaryAccuracy: document.getElementById('summary-accuracy'),
        summaryDuration: document.getElementById('summary-duration'),
        summaryStreak: document.getElementById('summary-streak'),
        summaryToughest: document.getElementById('summary-toughest'),
        // Audio loading overlay
        loadingOverlay: document.getElementById('loading-overlay'),
        progressFill: document.getElementById('progress-fill'),
        loadingCount: document.getElementById('loading-count')
    };
}

/**
 * Show the end-of-session summary card
 * @param {Object} DOM - DOM element references
 * @param {{total: number, accuracy: number, durationText: string, bestStreak: number, toughest: string[]}} summary - Session results
 */
export function showSessionSummary(DOM, summary) {
    if (!DOM.sessionSummary) return;

    if (DOM.summaryChars) DOM.summaryChars.textContent = summary.total;
    if (DOM.summaryAccuracy) DOM.summaryAccuracy.textContent = summary.accuracy + '%';
    if (DOM.summaryDuration) DOM.summaryDuration.textContent = summary.durationText;
    if (DOM.summaryStreak) DOM.summaryStreak.textContent = summary.bestStreak;

    if (DOM.summaryToughest) {
        if (summary.toughest.length > 0) {
            DOM.summaryToughest.textContent = `Most challenging: ${summary.toughest.join(', ')}`;
            DOM.summaryToughest.hidden = false;
        } else {
            DOM.summaryToughest.hidden = true;
        }
    }

    DOM.sessionSummary.hidden = false;
}

/**
 * Hide the end-of-session summary card
 * @param {Object} DOM - DOM element references
 */
export function hideSessionSummary(DOM) {
    if (DOM.sessionSummary) {
        DOM.sessionSummary.hidden = true;
    }
}

/**
 * Show the audio loading overlay
 * @param {Object} DOM - DOM element references
 */
export function showLoadingOverlay(DOM) {
    if (DOM.loadingOverlay) {
        DOM.loadingOverlay.classList.add('show');
    }
}

/**
 * Hide the audio loading overlay
 * @param {Object} DOM - DOM element references
 */
export function hideLoadingOverlay(DOM) {
    if (DOM.loadingOverlay) {
        DOM.loadingOverlay.classList.remove('show');
    }
}

/**
 * Update the audio loading progress display
 * @param {Object} DOM - DOM element references
 * @param {number} loaded - Buffers decoded so far
 * @param {number} total - Total buffers to decode
 */
export function updateLoadingProgress(DOM, loaded, total) {
    if (DOM.progressFill && total > 0) {
        DOM.progressFill.style.width = `${Math.round((loaded / total) * 100)}%`;
    }
    if (DOM.loadingCount) {
        DOM.loadingCount.textContent = `${loaded}/${total}`;
    }
}

/**
 * Flash the practice card green or red after an attempt is recorded
 * @param {Object} DOM - DOM element references
 * @param {boolean} correct - Whether the attempt was correct
 * @returns {Function|null} Cleanup function that removes the flash class
 */
export function flashResult(DOM, correct) {
    if (!DOM.practiceCard) return null;

    const className = correct ? 'result-correct' : 'result-incorrect';
    DOM.practiceCard.classList.remove('result-correct', 'result-incorrect');
    DOM.practiceCard.classList.add(className);

    return () => DOM.practiceCard.classList.remove(className);
}

// Element focused before the settings dialog opened, restored on close
let lastFocusedElement = null;

/**
 * Keep Tab focus inside the settings dialog while it is open
 */
function trapSettingsFocus(e) {
    if (e.key !== 'Tab') return;

    const panel = e.currentTarget;
    const focusables = panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
}

/**
 * Show settings panel
 * @param {Object} DOM - DOM element references
 */
export function showSettings(DOM) {
    if (!DOM.settingsPanel || DOM.settingsPanel.classList.contains('show')) return;

    lastFocusedElement = document.activeElement;

    DOM.settingsPanel.removeAttribute('inert');
    DOM.settingsPanel.classList.add('show');
    DOM.settingsPanel.addEventListener('keydown', trapSettingsFocus);

    // Also show overlay
    const overlay = document.getElementById('overlay');
    if (overlay) {
        overlay.classList.add('show');
    }

    if (DOM.closeSettings) {
        DOM.closeSettings.focus();
    }
}

/**
 * Hide settings panel
 * @param {Object} DOM - DOM element references
 */
export function hideSettings(DOM) {
    if (!DOM.settingsPanel || !DOM.settingsPanel.classList.contains('show')) return;

    DOM.settingsPanel.classList.remove('show');
    DOM.settingsPanel.setAttribute('inert', '');
    DOM.settingsPanel.removeEventListener('keydown', trapSettingsFocus);

    // Also hide overlay
    const overlay = document.getElementById('overlay');
    if (overlay) {
        overlay.classList.remove('show');
    }

    // Return focus to wherever the user was before opening the dialog
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
    }
    lastFocusedElement = null;
}

/**
 * Update timer display
 * @param {Object} DOM - DOM element references
 * @param {string} timeString - Formatted time string
 */
export function updateTimerDisplay(DOM, timeString) {
    if (DOM.timerDisplay) {
        DOM.timerDisplay.textContent = timeString;
    }
}

/**
 * Update recognition time display
 * @param {Object} DOM - DOM element references
 * @param {number} time - Recognition time in ms
 */
export function updateRecognitionDisplay(DOM, time) {
    if (DOM.pauseDisplay) {
        DOM.pauseDisplay.textContent = time + ' ms';
    }
    if (DOM.settingsRecognition) {
        DOM.settingsRecognition.textContent = time + ' ms';
    }
}

/**
 * Update speed display
 * @param {Object} DOM - DOM element references
 * @param {number} wpm - Speed in words per minute
 */
export function updateSpeedDisplay(DOM, wpm) {
    if (DOM.speedDisplay) {
        DOM.speedDisplay.textContent = wpm + ' WPM';
    }
    if (DOM.settingsSpeed) {
        DOM.settingsSpeed.textContent = wpm + ' wpm';
    }
}

/**
 * Show character display
 * @param {Object} DOM - DOM element references
 * @param {string} char - Character to display
 */
export function showCharacter(DOM, char) {
    if (DOM.charDisplay) {
        DOM.charDisplay.textContent = char;
        DOM.charDisplay.classList.add('visible');
    }
    if (DOM.listeningIndicator) {
        DOM.listeningIndicator.classList.remove('visible');
    }
}

/**
 * Hide character display
 * @param {Object} DOM - DOM element references
 */
export function hideCharacter(DOM) {
    if (DOM.charDisplay) {
        DOM.charDisplay.textContent = '';
        DOM.charDisplay.classList.remove('visible');
    }
}

/**
 * Show listening indicator
 * @param {Object} DOM - DOM element references
 */
export function showListeningIndicator(DOM) {
    if (DOM.listeningIndicator) {
        DOM.listeningIndicator.classList.add('visible');
    }
}

/**
 * Update training UI state
 * @param {Object} DOM - DOM element references
 * @param {boolean} isPlaying - Whether training is active
 */
export function updatePracticeState(DOM, isPlaying) {
    if (isPlaying) {
        if (DOM.startButton) DOM.startButton.style.display = 'none';
        if (DOM.practiceContent) DOM.practiceContent.style.display = 'flex';
        if (DOM.practiceCard) DOM.practiceCard.classList.remove('waiting-start');
        if (DOM.oopsButton) DOM.oopsButton.disabled = false;
        // Hide welcome box and show gauge container when practice starts
        if (DOM.welcomeBox) DOM.welcomeBox.style.display = 'none';
        if (DOM.gaugeContainer) DOM.gaugeContainer.style.display = 'block';
        document.body.classList.add('is-playing');
        document.body.classList.add('has-practiced');
    } else {
        if (DOM.startButton) DOM.startButton.style.display = 'block';
        if (DOM.practiceContent) DOM.practiceContent.style.display = 'none';
        if (DOM.practiceCard) DOM.practiceCard.classList.add('waiting-start');
        document.body.classList.remove('is-playing');
        hideCharacter(DOM);
        if (DOM.listeningIndicator) DOM.listeningIndicator.classList.remove('visible');
        if (DOM.oopsButton) {
            DOM.oopsButton.classList.remove('oops-marked');
            DOM.oopsButton.setAttribute('aria-pressed', 'false');
        }
    }
}

/**
 * Update oops button display
 * @param {Object} DOM - DOM element references
 * @param {string} phase - Current phase
 * @param {string} currentChar - Current character
 * @param {string} previousChar - Previous character
 * @param {string} markedCharacter - Marked character
 */
export function updateOopsButton(DOM, phase, currentChar, previousChar, markedCharacter) {
    if (!DOM.oopsButton || !DOM.oopsChar) return;

    // Clear marked state when appropriate
    if (phase === 'reveal' && currentChar !== markedCharacter) {
        DOM.oopsButton.classList.remove('oops-marked');
        DOM.oopsButton.setAttribute('aria-pressed', 'false');
    }

    // Update button text based on phase (the thumbs-down SVG lives in the markup)
    let targetChar = null;
    if (phase === 'morse' || phase === 'recognition') {
        targetChar = previousChar || null;
        DOM.oopsChar.textContent = targetChar || '–';
        DOM.oopsButton.disabled = !targetChar;
    } else if ((phase === 'reveal' || phase === 'success' || phase === 'oops-window') && currentChar) {
        targetChar = currentChar;
        DOM.oopsChar.textContent = targetChar;
        DOM.oopsButton.disabled = false;
    }

    DOM.oopsButton.setAttribute(
        'aria-label',
        targetChar ? `Mark ${targetChar} as missed` : 'Mark character as missed'
    );
}

/**
 * Mark oops button as pressed
 * @param {Object} DOM - DOM element references
 */
export function markOopsButton(DOM) {
    if (DOM.oopsButton) {
        DOM.oopsButton.classList.add('oops-marked', 'pressed');
        DOM.oopsButton.setAttribute('aria-pressed', 'true');
        setTimeout(() => {
            DOM.oopsButton.classList.remove('pressed');
        }, 200);
    }
}

/**
 * Create ripple effect on button
 * @param {HTMLElement} button - Button element
 * @param {MouseEvent} event - Mouse event
 */
export function createRipple(button, event) {
    // Remove any existing ripples
    const existingRipples = button.querySelectorAll('.ripple');
    existingRipples.forEach(ripple => ripple.remove());

    // Create new ripple
    const ripple = document.createElement('span');
    ripple.className = 'ripple';

    // Calculate position
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event ? event.clientX - rect.left - size / 2 : rect.width / 2 - size / 2;
    const y = event ? event.clientY - rect.top - size / 2 : rect.height / 2 - size / 2;

    // Set ripple size and position
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';

    // Add ripple to container
    const rippleContainer = button.querySelector('.ripple-container');
    if (rippleContainer) {
        rippleContainer.appendChild(ripple);
    }

    // Remove ripple after animation
    setTimeout(() => ripple.remove(), 600);
}

// Handle for the running waveform animation frame loop
let waveformAnimationId = null;

/**
 * Animate the waveform for the duration of a character's playback.
 * Every bar pulses identically regardless of symbol - the indicator is
 * deliberately amplitude- and symbol-agnostic so the visual never reveals
 * the dit/dah pattern (training is by sound only).
 * @param {string} morseCode - Morse code pattern (used only for duration)
 * @param {Object} timings - Timing information
 */
export function animateWaveform(morseCode, timings) {
    const waveformBars = document.querySelectorAll('.waveform-bar');
    if (!waveformBars.length) return;

    stopWaveform();

    // Total playback duration of the pattern
    let totalMs = 0;
    for (let i = 0; i < morseCode.length; i++) {
        totalMs += morseCode[i] === '.' ? timings.dotDurationMs : timings.dashDurationMs;
        if (i < morseCode.length - 1) {
            totalMs += timings.symbolSpacingMs;
        }
    }

    waveformBars.forEach(bar => bar.classList.add('active'));

    const startTime = performance.now();
    const tick = (now) => {
        if (now - startTime >= totalMs) {
            waveformBars.forEach(bar => bar.classList.remove('active'));
            waveformAnimationId = null;
            return;
        }
        waveformAnimationId = requestAnimationFrame(tick);
    };

    waveformAnimationId = requestAnimationFrame(tick);
}

/**
 * Stop the waveform animation and clear any active bar states
 */
export function stopWaveform() {
    if (waveformAnimationId !== null) {
        cancelAnimationFrame(waveformAnimationId);
        waveformAnimationId = null;
    }
    document.querySelectorAll('.waveform-bar').forEach(bar => {
        bar.classList.remove('active');
    });
}

/**
 * Create SVG for semicircular training zone gauge
 * @param {string} activeZone - Currently active zone
 * @returns {string} SVG markup
 */
function createSemicircularGaugeSVG(activeZone = '') {
    const width = 320;
    const height = 180;
    const centerX = width / 2;
    const centerY = height - 34;
    const radius = 125;
    const innerRadius = 91;

    // Generate unique ID for this gauge instance
    const uniqueId = Math.random().toString(36).slice(2, 11);

    // Zone boundaries (full semicircle: -90 to 90 degrees, three equal zones)
    const startAngle = -90;
    const tooHardEnd = -30;
    const learningEnd = 30;
    const tooEasyEnd = 90;

    // Convert degrees to radians (adjusting for SVG coordinate system)
    const degToRad = (deg) => ((deg - 90) * Math.PI) / 180;
    
    // Helper function to create arc path
    const createArcPath = (startDeg, endDeg, r, ir) => {
        const start = degToRad(startDeg);
        const end = degToRad(endDeg);
        
        const x1 = centerX + r * Math.cos(start);
        const y1 = centerY + r * Math.sin(start);
        const x2 = centerX + r * Math.cos(end);
        const y2 = centerY + r * Math.sin(end);
        
        const ix1 = centerX + ir * Math.cos(start);
        const iy1 = centerY + ir * Math.sin(start);
        const ix2 = centerX + ir * Math.cos(end);
        const iy2 = centerY + ir * Math.sin(end);
        
        const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
        
        return `
            M ${x1} ${y1}
            A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}
            L ${ix2} ${iy2}
            A ${ir} ${ir} 0 ${largeArc} 0 ${ix1} ${iy1}
            Z
        `;
    };
    
    return `
        <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" class="gauge-svg" overflow="hidden">
            <defs>
                <!-- Define gradients for zones -->
                <linearGradient id="tooHardGradient-${uniqueId}" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:var(--accent-red);stop-opacity:0.5" />
                    <stop offset="100%" style="stop-color:var(--accent-red);stop-opacity:0.3" />
                </linearGradient>
                <linearGradient id="learningGradient-${uniqueId}" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:var(--accent-green);stop-opacity:0.55" />
                    <stop offset="100%" style="stop-color:var(--accent-green);stop-opacity:0.35" />
                </linearGradient>
                <linearGradient id="tooEasyGradient-${uniqueId}" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:var(--accent-blue);stop-opacity:0.5" />
                    <stop offset="100%" style="stop-color:var(--accent-blue);stop-opacity:0.3" />
                </linearGradient>
                <!-- Subtle pattern for zones -->
                <pattern id="zonePattern-${uniqueId}" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="0.5" fill="rgba(255, 255, 255, 0.05)" />
                </pattern>
                
                <!-- Glow filters for active zones - iOS compatible -->
                <filter id="learningGlow-${uniqueId}" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="8" result="blur"/>
                    <feFlood style="flood-color: var(--accent-green)" flood-opacity="0.6"/>
                    <feComposite in2="blur" operator="in"/>
                    <feMerge>
                        <feMergeNode/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
                
                <filter id="tooEasyGlow-${uniqueId}" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="8" result="blur"/>
                    <feFlood style="flood-color: var(--accent-blue)" flood-opacity="0.6"/>
                    <feComposite in2="blur" operator="in"/>
                    <feMerge>
                        <feMergeNode/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
                
                <filter id="tooHardGlow-${uniqueId}" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="8" result="blur"/>
                    <feFlood style="flood-color: var(--accent-red)" flood-opacity="0.6"/>
                    <feComposite in2="blur" operator="in"/>
                    <feMerge>
                        <feMergeNode/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
            </defs>
            
            <!-- Background arc with subtle gradient -->
            <path d="M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}" 
                  fill="none" stroke="var(--border-subtle)" stroke-width="1.5" opacity="0.2" />
            <path d="M ${centerX - innerRadius} ${centerY} A ${innerRadius} ${innerRadius} 0 0 1 ${centerX + innerRadius} ${centerY}" 
                  fill="none" stroke="var(--border-subtle)" stroke-width="1.5" opacity="0.2" />
            
            <!-- Glow layers using multiple strokes - all included but hidden -->
            <g class="glow-layer glow-layer-too-hard" style="display: ${activeZone === 'too-hard' ? 'block' : 'none'}">
                <path d="${createArcPath(startAngle, tooHardEnd, radius + 4, innerRadius - 4)}" 
                      fill="none" style="stroke: rgba(var(--accent-red-rgb), 0.2)" stroke-width="8" />
                <path d="${createArcPath(startAngle, tooHardEnd, radius + 2, innerRadius - 2)}" 
                      fill="none" style="stroke: rgba(var(--accent-red-rgb), 0.3)" stroke-width="6" />
                <path d="${createArcPath(startAngle, tooHardEnd, radius + 1, innerRadius - 1)}" 
                      fill="none" style="stroke: rgba(var(--accent-red-rgb), 0.4)" stroke-width="4" />
            </g>
            <g class="glow-layer glow-layer-learning" style="display: ${activeZone === 'learning' ? 'block' : 'none'}">
                <path d="${createArcPath(tooHardEnd, learningEnd, radius + 4, innerRadius - 4)}" 
                      fill="none" style="stroke: rgba(var(--accent-green-rgb), 0.2)" stroke-width="8" />
                <path d="${createArcPath(tooHardEnd, learningEnd, radius + 2, innerRadius - 2)}" 
                      fill="none" style="stroke: rgba(var(--accent-green-rgb), 0.3)" stroke-width="6" />
                <path d="${createArcPath(tooHardEnd, learningEnd, radius + 1, innerRadius - 1)}" 
                      fill="none" style="stroke: rgba(var(--accent-green-rgb), 0.4)" stroke-width="4" />
            </g>
            <g class="glow-layer glow-layer-too-easy" style="display: ${activeZone === 'too-easy' ? 'block' : 'none'}">
                <path d="${createArcPath(learningEnd, tooEasyEnd, radius + 4, innerRadius - 4)}" 
                      fill="none" style="stroke: rgba(var(--accent-blue-rgb), 0.2)" stroke-width="8" />
                <path d="${createArcPath(learningEnd, tooEasyEnd, radius + 2, innerRadius - 2)}" 
                      fill="none" style="stroke: rgba(var(--accent-blue-rgb), 0.3)" stroke-width="6" />
                <path d="${createArcPath(learningEnd, tooEasyEnd, radius + 1, innerRadius - 1)}" 
                      fill="none" style="stroke: rgba(var(--accent-blue-rgb), 0.4)" stroke-width="4" />
            </g>
            
            <!-- Arc segments for zones with gradients -->
            <path d="${createArcPath(startAngle, tooHardEnd, radius, innerRadius)}" 
                  fill="url(#tooHardGradient-${uniqueId})" class="gauge-zone-arc too-hard-arc" />
            <path d="${createArcPath(tooHardEnd, learningEnd, radius, innerRadius)}" 
                  fill="url(#learningGradient-${uniqueId})" class="gauge-zone-arc learning-arc" />
            <path d="${createArcPath(learningEnd, tooEasyEnd, radius, innerRadius)}" 
                  fill="url(#tooEasyGradient-${uniqueId})" class="gauge-zone-arc too-easy-arc" />
            
            <!-- Zone dividers -->
            <line x1="${centerX + innerRadius * Math.cos(degToRad(tooHardEnd))}" 
                  y1="${centerY + innerRadius * Math.sin(degToRad(tooHardEnd))}"
                  x2="${centerX + radius * Math.cos(degToRad(tooHardEnd))}" 
                  y2="${centerY + radius * Math.sin(degToRad(tooHardEnd))}"
                  stroke="var(--border-subtle)" stroke-width="1" opacity="0.5" />
            <line x1="${centerX + innerRadius * Math.cos(degToRad(learningEnd))}" 
                  y1="${centerY + innerRadius * Math.sin(degToRad(learningEnd))}"
                  x2="${centerX + radius * Math.cos(degToRad(learningEnd))}" 
                  y2="${centerY + radius * Math.sin(degToRad(learningEnd))}"
                  stroke="var(--border-subtle)" stroke-width="1" opacity="0.5" />
            
            <!-- Zone labels -->
            <defs>
                <!-- Define paths for text to follow - centered radially in each zone -->
                <!-- Text path radius adjusted for visual centering (slightly inward from mathematical center) -->
                <!-- Too Hard zone: -90° to -30° (center at -60°) -->
                <path id="tooHardPath-${uniqueId}" d="M ${centerX + (innerRadius + (radius - innerRadius) * 0.4) * Math.cos(degToRad(-80))} ${centerY + (innerRadius + (radius - innerRadius) * 0.4) * Math.sin(degToRad(-80))} A ${innerRadius + (radius - innerRadius) * 0.4} ${innerRadius + (radius - innerRadius) * 0.4} 0 0 1 ${centerX + (innerRadius + (radius - innerRadius) * 0.4) * Math.cos(degToRad(-40))} ${centerY + (innerRadius + (radius - innerRadius) * 0.4) * Math.sin(degToRad(-40))}" />
                <!-- Optimal zone: -30° to 30° (center at 0°) -->
                <path id="optimalPath-${uniqueId}" d="M ${centerX + (innerRadius + (radius - innerRadius) * 0.4) * Math.cos(degToRad(-20))} ${centerY + (innerRadius + (radius - innerRadius) * 0.4) * Math.sin(degToRad(-20))} A ${innerRadius + (radius - innerRadius) * 0.4} ${innerRadius + (radius - innerRadius) * 0.4} 0 0 1 ${centerX + (innerRadius + (radius - innerRadius) * 0.4) * Math.cos(degToRad(20))} ${centerY + (innerRadius + (radius - innerRadius) * 0.4) * Math.sin(degToRad(20))}" />
                <!-- Too Easy zone: 30° to 90° (center at 60°) -->
                <path id="tooEasyPath-${uniqueId}" d="M ${centerX + (innerRadius + (radius - innerRadius) * 0.4) * Math.cos(degToRad(40))} ${centerY + (innerRadius + (radius - innerRadius) * 0.4) * Math.sin(degToRad(40))} A ${innerRadius + (radius - innerRadius) * 0.4} ${innerRadius + (radius - innerRadius) * 0.4} 0 0 1 ${centerX + (innerRadius + (radius - innerRadius) * 0.4) * Math.cos(degToRad(80))} ${centerY + (innerRadius + (radius - innerRadius) * 0.4) * Math.sin(degToRad(80))}" />
            </defs>
            
            <!-- Text labels -->
            <text class="gauge-label" dominant-baseline="central">
                <textPath href="#tooHardPath-${uniqueId}" startOffset="50%" text-anchor="middle">TOO HARD</textPath>
            </text>
            <text class="gauge-label training-zone" dominant-baseline="central">
                <textPath href="#optimalPath-${uniqueId}" startOffset="50%" text-anchor="middle">OPTIMAL</textPath>
            </text>
            <text class="gauge-label" dominant-baseline="central">
                <textPath href="#tooEasyPath-${uniqueId}" startOffset="50%" text-anchor="middle">TOO EASY</textPath>
            </text>
            
            <!-- Gauge needle with enhanced design -->
            <g class="gauge-needle gauge-needle-animated"
               style="transform-origin: ${centerX}px ${centerY}px;">
                <!-- Needle shadow -->
                <path d="M ${centerX - 4} ${centerY} L ${centerX} ${centerY - radius + 25} L ${centerX + 4} ${centerY} Z" 
                      fill="rgba(0, 0, 0, 0.2)" 
                      transform="translate(2, 2)" />
                <!-- Needle triangle -->
                <path d="M ${centerX - 4} ${centerY} L ${centerX} ${centerY - radius + 25} L ${centerX + 4} ${centerY} Z" 
                      fill="var(--text-primary)" 
                      class="gauge-needle-triangle" />
                <!-- Needle line for additional emphasis -->
                <line x1="${centerX}" y1="${centerY - 10}" 
                      x2="${centerX}" y2="${centerY - radius + 25}" 
                      stroke="var(--text-primary)" 
                      stroke-width="2" 
                      opacity="0.8"
                      class="gauge-needle-line" />
                <!-- Center cap with gradient -->
                <circle cx="${centerX}" cy="${centerY}" r="8" fill="var(--bg-primary)" stroke="var(--text-primary)" stroke-width="2" />
                <circle cx="${centerX}" cy="${centerY}" r="4" fill="var(--text-primary)" />
            </g>
        </svg>
    `;
}

// Store previous accuracy for gauge animation
// Default to 80% (center of training zone) for initial display
let previousGaugeAccuracy = 80;

// Cached references into the gauge subtree, populated by createNewGauge
let gaugeRefs = null;

/**
 * Update training zone gauge
 * @param {Object} DOM - DOM element references
 * @param {number} accuracy - Current accuracy
 * @param {string} zone - Current zone ('too-easy', 'learning', 'too-hard')
 * @param {number} recognitionTime - Current recognition time in ms
 * @param {Object} adjustments - Recommended adjustments {decrease: ms, increase: ms}
 * @param {Object} recommendation - Recommendation data from getSpeedRecommendationRobust
 * @param {Object} trend - Trend data {direction: string, strength: number}
 * @param {number} attemptCount - Total attempts in session
 * @param {number} attemptsSinceChange - Attempts since last recognition time change
 */
export function updateTrainingZoneGauge(DOM, accuracy, zone, recognitionTime, adjustments, recommendation = null, trend = null, attemptCount = 0, attemptsSinceChange = null) {
    if (!DOM.gaugeContent) return;

    if (gaugeRefs && gaugeRefs.needle && gaugeRefs.needle.isConnected) {
        updateExistingGauge(zone, accuracy, recognitionTime, adjustments, recommendation, trend, attemptCount, attemptsSinceChange);
    } else {
        createNewGauge(DOM, accuracy, zone, recognitionTime, adjustments, recommendation, trend, attemptCount, attemptsSinceChange);
    }

    // Update previous accuracy for next animation
    previousGaugeAccuracy = accuracy;
}

function updateExistingGauge(zone, accuracy, recognitionTime, adjustments, recommendation, trend, attemptCount, attemptsSinceChange) {
    // Show only the glow layers for the active zone
    gaugeRefs.glowLayers.forEach(layer => {
        layer.style.display = layer.classList.contains(`glow-layer-${zone}`) ? 'block' : 'none';
    });

    // Update needle rotation
    const needle = gaugeRefs.needle;
    needle.style.transition = 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
    needle.style.transform = `rotate(${accuracyToAngle(accuracy)}deg)`;

    // Update wrapper zone class
    if (gaugeRefs.wrapper) {
        gaugeRefs.wrapper.className = `gauge-wrapper zone-${zone}`;
    }

    // Update recognition controls
    updateRecognitionControls(zone, recognitionTime, adjustments, recommendation, attemptCount, attemptsSinceChange, trend);
}

function createNewGauge(DOM, accuracy, zone, recognitionTime, adjustments, recommendation, trend, attemptCount, attemptsSinceChange) {
    // Create gauge SVG
    const gaugeSvg = createSemicircularGaugeSVG(zone);

    DOM.gaugeContent.innerHTML = `
        <div class="gauge-wrapper zone-${zone}">
            <div class="gauge-section">
                ${gaugeSvg}
            </div>
            <div class="zone-status-message">
                <span class="zone-status-text"></span>
            </div>
            <div class="recognition-controls">
                <button class="recognition-recommendation-btn" disabled>
                    <span class="recommendation-content">500ms</span>
                </button>
            </div>
        </div>
    `;

    // Cache gauge subtree references for subsequent updates
    gaugeRefs = {
        needle: DOM.gaugeContent.querySelector('.gauge-needle-animated'),
        wrapper: DOM.gaugeContent.querySelector('.gauge-wrapper'),
        glowLayers: Array.from(DOM.gaugeContent.querySelectorAll('.glow-layer')),
        recommendationBtn: DOM.gaugeContent.querySelector('.recognition-recommendation-btn'),
        recommendationContent: DOM.gaugeContent.querySelector('.recommendation-content'),
        zoneStatusText: DOM.gaugeContent.querySelector('.zone-status-text')
    };

    // Show gauge container and hide welcome box
    DOM.gaugeContainer.style.display = 'block';
    if (DOM.welcomeBox) {
        DOM.welcomeBox.style.display = 'none';
    }

    // Update recognition controls visibility
    updateRecognitionControls(zone, recognitionTime, adjustments, recommendation, attemptCount, attemptsSinceChange, trend);

    // Animate the needle from the previous accuracy to the new one
    const needle = gaugeRefs.needle;
    if (needle) {
        // Set initial position without transition
        needle.style.transition = 'none';
        needle.style.transform = `rotate(${accuracyToAngle(previousGaugeAccuracy)}deg)`;

        // Force reflow so the initial position applies before animating
        needle.offsetHeight;

        // Animate to target position
        needle.style.transition = 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        needle.style.transform = `rotate(${accuracyToAngle(accuracy)}deg)`;
    }
}

/**
 * Get appropriate training message based on current state
 * @param {string} zone - Current zone ('too-easy', 'learning', 'too-hard')
 * @param {number} attemptCount - Total attempts in session
 * @param {number} attemptsSinceChange - Attempts since last recognition time change
 * @param {Object} recommendation - Recommendation object from stats
 * @param {Object} trend - Trend data {direction: string, strength: number}
 * @returns {string} Message to display
 */
function getTrainingMessage(zone, attemptCount, attemptsSinceChange, recommendation, trend) {
    // Priority 1: Recommendations
    if (recommendation && recommendation.show) {
        if (zone === 'too-easy') {
            return 'Too easy - speed up for optimal training';
        } else if (zone === 'too-hard') {
            return 'Too hard - slow down for optimal training';
        }
    }
    
    // Priority 2: Cooldown period after recognition time change
    if (attemptsSinceChange !== null && attemptsSinceChange > 0 && attemptsSinceChange <= 12) {
        if (attemptsSinceChange <= 4) {
            return 'Adjusting to new timing...';
        } else if (attemptsSinceChange <= 8) {
            return 'Finding new rhythm...';
        } else {
            return 'Almost calibrated...';
        }
    }
    
    // Priority 3: Building baseline (early attempts)
    if (attemptCount < 12) {
        if (attemptCount === 0) {
            return 'Ready to train!';
        } else if (attemptCount <= 3) {
            return 'Getting started...';
        } else if (attemptCount <= 7) {
            return 'Building baseline...';
        } else {
            return 'Finding your rhythm';
        }
    }
    
    // Priority 4: Zone-based messages
    const trendArrow = (trend && trend.direction === 'up' && trend.strength > 0.1) ? ' ↑' : '';
    
    if (zone === 'learning') {
        // Rotate between messages for variety
        const messages = [
            'Perfect training pace!',
            'In the zone!',
            'Excellent rhythm!'
        ];
        const index = Math.floor(attemptCount / 10) % messages.length;
        return messages[index] + trendArrow;
    } else if (zone === 'too-easy') {
        const messages = [
            'Mastering these patterns!',
            'Making it look easy!',
            'Strong performance!'
        ];
        const index = Math.floor(attemptCount / 10) % messages.length;
        return messages[index] + trendArrow;
    } else if (zone === 'too-hard') {
        const messages = [
            'Keep listening carefully',
            'Building recognition',
            'Focus on the sounds'
        ];
        const index = Math.floor(attemptCount / 10) % messages.length;
        return messages[index] + trendArrow;
    }
    
    // Fallback (should never reach here)
    return 'Keep practicing!';
}

// Last message announced to screen readers, to avoid repeat announcements
let lastAnnouncedStatus = '';

function updateRecognitionControls(zone, recognitionTime, adjustments, recommendation = null, attemptCount = 0, attemptsSinceChange = null, trend = null) {
    const { recommendationBtn, recommendationContent, zoneStatusText } = gaugeRefs;

    // Update zone status message - always show a message
    if (zoneStatusText) {
        const statusMessage = getTrainingMessage(zone, attemptCount, attemptsSinceChange, recommendation, trend);

        // Determine status class based on zone
        let statusClass = '';
        if (zone === 'learning') {
            statusClass = 'optimal';
        } else if (zone === 'too-easy') {
            statusClass = 'too-easy';
        } else if (zone === 'too-hard') {
            statusClass = 'too-hard';
        }

        zoneStatusText.textContent = statusMessage;
        zoneStatusText.className = `zone-status-text ${statusClass}`;

        // Announce zone changes politely to screen readers (once per change)
        const srStatus = document.getElementById('sr-status');
        if (srStatus && statusMessage !== lastAnnouncedStatus) {
            srStatus.textContent = statusMessage;
            lastAnnouncedStatus = statusMessage;
        }
    }
    
    if (recommendationBtn && recommendationContent) {
        // Update button based on recommendation
        if (recommendation && recommendation.show) {
            // Enable button and show adjustment
            recommendationBtn.disabled = false;
            recommendationBtn.classList.remove('training-well');
            
            const newTime = recommendation.action === 'decrease' 
                ? recognitionTime - adjustments.decrease 
                : recognitionTime + adjustments.increase;
            
            // Set data attributes for the event handler
            recommendationBtn.dataset.adjustment = recommendation.action === 'decrease' 
                ? -adjustments.decrease 
                : adjustments.increase;
            
            // Update button content with arrow
            recommendationContent.textContent = `${recognitionTime}ms → ${newTime}ms`;
            
            // Add appropriate class for styling
            recommendationBtn.classList.add(recommendation.action);
        } else {
            // Disable button when training well - show current recognition time
            recommendationBtn.disabled = true;
            recommendationBtn.classList.add('training-well');
            recommendationBtn.classList.remove('increase', 'decrease');
            recommendationBtn.removeAttribute('data-adjustment');
            recommendationContent.textContent = `${recognitionTime}ms`;
        }
    }
}

/**
 * Generate helpful confusion text based on the reason
 * @param {string} confusedWith - The character it's confused with
 * @param {string} reason - The reason for confusion
 * @returns {string} HTML text describing the confusion
 */
function getConfusionText(confusedWith, reason) {
    // Keep it simple - just show the relationship without analytical tips
    const reasonText = {
        'Mirror sounds': `Mirror sound of`,
        'Brief sounds': `Brief like`,
        'Similar flow': `Flows like`,
        'Similar cadence': `Cadence like`,
        'Similar rhythm': `Rhythm like`,
        'Extended pattern': `Extended from`,
        'Shortened pattern': `Shortened from`,
        'Similar sound': `Sounds like`,
        'Similar at speed': `Fast sounds like`,
        'Complex pattern': `Complex like`,
        'Flowing pattern': `Flows like`,
        'Mixed pattern': `Mixed like`,
        'Alternating sound': `Alternates like`,
        'Rolling sound': `Rolls like`,
        'Complex rhythm': `Complex like`,
        'Number pattern': `Number like`,
        'Long pattern': `Long like`
    };
    
    const prefix = reasonText[reason] || 'May confuse with';
    return `${prefix} <strong>${confusedWith}</strong>`;
}

/**
 * Update focus queue with struggling characters
 * @param {Object} DOM - DOM element references
 * @param {Array} priorityChars - Array of priority characters from getCharacterPriorityScores
 * @param {Object} confusionMatrix - Confusion matrix for showing confusion pairs
 */
export function updateFocusQueue(DOM, priorityChars, confusionMatrix) {
    if (!DOM.focusQueueGrid) return;
    
    // Take top 5 characters that need work (show all, not just practiced)
    const topChars = priorityChars.slice(0, 5);
    
    // Always show the focus queue
    DOM.focusQueue.style.display = 'block';
    
    if (topChars.length === 0) {
        // Show success state when no challenging characters
        DOM.focusQueueGrid.innerHTML = `
            <div class="focus-success">
                <svg class="success-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 6L9 17l-5-5"/>
                </svg>
                <p class="success-message">No challenging characters!</p>
            </div>
        `;
        return;
    }
    
    DOM.focusQueueGrid.innerHTML = topChars.map(charData => {
        const { char, accuracy, needsReview } = charData;
        
        // Find most confused with character
        let confusedWith = '';
        let confusionReason = '';
        
        // Check actual confusion data first
        if (confusionMatrix[char]) {
            const confusions = Object.entries(confusionMatrix[char])
                .sort(([,a], [,b]) => b - a);
            if (confusions.length > 0) {
                confusedWith = confusions[0][0];
            }
        }
        
        // Fall back to common confusions if no actual data
        if (!confusedWith && COMMON_CONFUSIONS[char]) {
            confusedWith = COMMON_CONFUSIONS[char].char;
            confusionReason = COMMON_CONFUSIONS[char].reason;
        }
        
        return `
            <div class="focus-char ${needsReview ? 'needs-review' : ''}">
                <div class="focus-char-letter">${char}</div>
                <div class="focus-char-stats">
                    <div class="focus-accuracy">
                        <div class="focus-accuracy-bar" style="width: ${accuracy}%"></div>
                    </div>
                    <div class="focus-accuracy-text">${accuracy}% accuracy</div>
                    ${confusedWith ? `
                        <div class="focus-confusion">
                            ${confusionReason
                                ? getConfusionText(confusedWith, confusionReason)
                                : `Often confused with <strong>${confusedWith}</strong>`
                            }
                        </div>
                    ` : ''}
                </div>
            </div>
        `;
    }).join('');
}


/**
 * Update recently practiced stream
 * @param {Object} DOM - DOM element references
 * @param {Array} recentAttempts - Array of recent attempt objects with char and correct properties
 * @param {Object} charStats - Character statistics for performance data
 */
export function updatePracticeStream(DOM, recentAttempts, charStats) {
    if (!DOM.practiceStreamItems) return;
    
    if (recentAttempts.length === 0) {
        DOM.practiceStreamItems.innerHTML = `
            <div class="stream-placeholder">
                <span class="loading-dots">•••</span>
            </div>
        `;
        return;
    }
    
    // Take last 15 attempts, reverse to show most recent first
    const streamAttempts = recentAttempts.slice(-15).reverse();
    
    DOM.practiceStreamItems.innerHTML = streamAttempts.map((attempt) => {
        const char = attempt.char;
        const stats = charStats[char];
        if (!stats) return '';

        const totalAttempts = stats.correct + stats.incorrect;
        const accuracy = totalAttempts > 0 
            ? Math.round((stats.correct / totalAttempts) * 100)
            : 0;
        
        // Use the actual result from this attempt
        const wasCorrect = attempt.correct;
        
        let statusClass = 'neutral';
        if (wasCorrect !== undefined) {
            statusClass = wasCorrect ? 'good' : 'struggling';
        } else {
            // Fallback to accuracy-based coloring if no result data
            if (accuracy >= 80) statusClass = 'good';
            else if (accuracy <= 50) statusClass = 'struggling';
        }
        
        return `
            <div class="stream-char ${statusClass}">
                <div class="stream-char-letter">${char}</div>
                <div class="stream-char-accuracy">${accuracy}%</div>
            </div>
        `;
    }).join('');
}

